import { AxiosError, isCancel } from "axios";

import { getReadableAxiosError } from "utils/alerts";
import { rollbarAndLogError, rollbarAndLogInfo, rollbarAndLogWarning } from "utils/logger";
import { isRetryableNetworkError } from "utils/network-errors";

export { isRetryableNetworkError } from "utils/network-errors";

function isAbortError(error: unknown): boolean {
  return (
    typeof error === "object" && error !== null && "name" in error && error.name === "AbortError"
  );
}

/** Plain Error message used by the collaboration API interceptor after normalizing axios cancel/abort. */
export const CANCELED_REQUEST_ERROR_MESSAGE = "Request canceled or aborted";

/** User navigation, inactive-tab aborts, or explicit axios cancel — log at info, not warning/error. */
export function isCanceledOrAbortedRequest(error: unknown): boolean {
  return (
    isCancel(error) ||
    isAbortError(error) ||
    (error instanceof Error &&
      (error.message === CANCELED_REQUEST_ERROR_MESSAGE ||
        error.message.includes("Request aborted")))
  );
}

/** Retryable network/timeout failures — warn, not error. Cancel/abort use {@link isCanceledOrAbortedRequest} + info. */
export function shouldReportFailureAsWarning(error: unknown): boolean {
  return isRetryableNetworkError(error);
}

/** Transient or expected failures already handled elsewhere — skip Rollbar uncaught/unhandled capture. */
export function shouldIgnoreUncaughtRollbarError(error: unknown): boolean {
  return isCanceledOrAbortedRequest(error) || isRetryableNetworkError(error);
}

function getApiFailureLogString(name: string, error: unknown, failureMessage?: string): string {
  if (failureMessage !== undefined) {
    return `${name} failed with message: ${failureMessage}`;
  }

  if (error instanceof AxiosError) {
    const message = getReadableAxiosError(error);
    return `${name} failed with message: ${message}`;
  }
  return name;
}

const reportApiError = (name: string, error: unknown, failureMessage?: string) => {
  rollbarAndLogError(getApiFailureLogString(name, error, failureMessage), error);
};

const reportApiWarning = (name: string, error: unknown, failureMessage?: string) => {
  rollbarAndLogWarning(getApiFailureLogString(name, error, failureMessage), error);
};

const reportApiInfo = (name: string, error: unknown, failureMessage?: string) => {
  rollbarAndLogInfo(getApiFailureLogString(name, error, failureMessage), error);
};

export const reportApiFailure = (name: string, error: unknown, failureMessage?: string) => {
  if (isCanceledOrAbortedRequest(error)) {
    reportApiInfo(name, error, failureMessage);
  } else if (shouldReportFailureAsWarning(error)) {
    reportApiWarning(name, error, failureMessage);
  } else {
    reportApiError(name, error, failureMessage);
  }
};

export async function withApiErrorReporting<T>(
  name: string,
  operation: () => Promise<T>,
): Promise<T> {
  try {
    return await operation();
  } catch (error) {
    reportApiFailure(name, error);
    throw error;
  }
}
